'use client'

import React from 'react'
import { cn } from '@/lib/utils'

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string
  size?: 'sm' | 'md'
  /** Figma uses round (100px) hit areas inside cards and square (8px) ones in the top nav. */
  shape?: 'round' | 'square'
  active?: boolean
  tone?: 'default' | 'muted'
  /** Render the single child (a Link/<a>) with the button styling instead of a <button>. */
  asChild?: boolean
}

/** 32px icon-only button with a 16px glyph (Figma "Frame 13"). */
const IconButton = React.forwardRef<HTMLButtonElement, Props>(
  (
    { className, label, size = 'sm', shape = 'round', active, tone = 'default', asChild = false, children, type = 'button', ...props },
    ref
  ) => {
    const classes = cn(
        'inline-flex shrink-0 items-center justify-center outline-none',
        'transition-[transform,background-color,color] duration-[160ms] ease-out-strong active:scale-[0.94]',
        'hover:bg-surface-2 dark:hover:bg-surface-3 data-[active=true]:bg-surface-2 dark:data-[active=true]:bg-surface-3',
        'focus-visible:ring-2 focus-visible:ring-brand/40',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
        size === 'sm' ? 'size-8 [&>svg]:size-4' : 'size-9 [&>svg]:size-[18px]',
        shape === 'round' ? 'rounded-full' : 'rounded-[8px]',
        tone === 'default' ? 'text-ink-2 hover:text-ink' : 'text-ink-4 hover:text-ink-2',
        className
      )

    if (asChild && React.isValidElement(children)) {
      const child = children as React.ReactElement<Record<string, unknown>>
      return React.cloneElement(child, {
        ...props,
        ref,
        'aria-label': label,
        title: label,
        'data-active': active ? 'true' : undefined,
        className: cn(classes, child.props.className as string | undefined),
      } as Record<string, unknown>)
    }

    return (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        title={label}
        data-active={active ? 'true' : undefined}
        className={classes}
        {...props}
      >
        {children}
      </button>
    )
  }
)
IconButton.displayName = 'IconButton'

export default IconButton
